import React, { useState, useMemo } from "react";
import { plateStockType } from "@apv-erp/status";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Printer,
  CheckCircle2,
  Clock,
  ShieldAlert,
  FileCheck,
  RotateCcw,
  Loader2,
  FileText,
  Download,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpcClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import ProofApprovalModal from "./ProofApprovalModal";
import MountPlateToMachineModal from "./MountPlateToMachineModal";
import PlateDamageRemakeModal from "./PlateDamageRemakeModal";
import PrintDialog from "@/components/shared/PrintDialog";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useConfirm } from "@/providers/ConfirmProvider";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { exportToExcel, type ExcelColumnDef } from "@/lib/excel.utils";

const DISPATCH_EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "plateKey", label: "Mã Bài Kẽm", width: 18 },
  { key: "productName", label: "Sản Phẩm / Bài In", width: 32 },
  { key: "customerName", label: "Khách Hàng", width: 26 },
  { key: "size", label: "Khổ Kẽm", width: 14 },
  { key: "colors", label: "Bộ Phân Màu", width: 26 },
  { key: "proof", label: "Duyệt Proof", width: 14 },
  { key: "machine", label: "Máy In", width: 20 },
  { key: "status", label: "Trạng Thái", width: 16 },
];

const toDispatchExportRow = (p: any) => ({
  plateKey: p.plateId || p.id,
  productName: p.productName || p.name || "",
  customerName: p.customerName || p.customer?.name || "",
  size: p.size || "",
  colors: (p.colors || []).join(", "),
  proof: p.proofStatus === "APPROVED" ? "Đã duyệt" : p.proofStatus === "REJECTED" ? "Từ chối" : "Chờ duyệt",
  machine: p.activeMachineName || "",
  status: p.status || "",
});

export default function CTPDispatchBoard() {
  const navigate = useNavigate();
  // Từ khoá lấy từ ô tìm kiếm chung trên header (?q=), trang không có ô tìm riêng
  const searchTerm: string = useRouterState({ select: (st) => String((st.location.search as any)?.q || "") });
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [selectedPlate, setSelectedPlate] = useState<any>(null);
  const [showProofModal, setShowProofModal] = useState(false);
  const [showMountModal, setShowMountModal] = useState(false);
  const [showDamageModal, setShowDamageModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Truy vấn danh sách kẽm CTP
  const { data: platesData, isLoading } = useQuery({
    // Dưới nhóm "plates": Ký proof / Lắp máy / Báo hỏng làm mới "plates" thì bảng tự cập nhật (FB-OUBS48)
    queryKey: ["plates", "dispatch-board"],
    queryFn: async () => {
      const res = await (trpcClient as any).technical.plates.list.query({
        limit: 200,
      });
      return (res?.items || res || []) as any[];
    },
  });

  const plates = (platesData || []) as any[];

  // Single-Row Inline Metrics
  const metrics = useMemo(() => {
    const total = plates.length;
    const pendingProof = plates.filter(
      (p) => !p.proofStatus || p.proofStatus === "PENDING"
    ).length;
    const proofApproved = plates.filter((p) => p.proofStatus === "APPROVED").length;
    const mountedInUse = plates.filter((p) => p.status === "IN_USE").length;
    const damagedRemake = plates.filter(
      (p) => p.status === "DAMAGED" || (p.remakeCount && p.remakeCount > 0)
    ).length;

    return { total, pendingProof, proofApproved, mountedInUse, damagedRemake };
  }, [plates]);

  // Bộ lọc tìm kiếm & trạng thái
  const filteredPlates = useMemo(() => {
    return plates.filter((p) => {
      const term = searchTerm.toLowerCase();
      const codeMatch = (p.plateId || p.id || "").toLowerCase().includes(term);
      const nameMatch = (p.productName || p.name || "").toLowerCase().includes(term);
      const machineMatch = (p.activeMachineName || p.machineName || "").toLowerCase().includes(term);

      if (searchTerm && !codeMatch && !nameMatch && !machineMatch) {
        return false;
      }

      if (statusFilter === "PENDING_PROOF") {
        return !p.proofStatus || p.proofStatus === "PENDING";
      }
      if (statusFilter === "PROOF_APPROVED") {
        return p.proofStatus === "APPROVED";
      }
      if (statusFilter === "IN_USE") {
        return p.status === "IN_USE";
      }
      if (statusFilter === "DAMAGED") {
        return p.status === "DAMAGED" || (p.remakeCount && p.remakeCount > 0);
      }

      return true;
    });
  }, [plates, searchTerm, statusFilter]);

  // ── Chọn nhiều dòng + thao tác hàng loạt ──
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();
  const rowIds = useMemo(() => filteredPlates.map((p: any) => p.plateId || p.id), [filteredPlates]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;
  const handleExportAll = () =>
    exportToExcel(filteredPlates.map(toDispatchExportRow), DISPATCH_EXPORT_COLUMNS, "dieu_phoi_kem_ctp");
  const bulkActions: BulkAction[] = selectedCount
    ? [
        {
          icon: <Download className="h-4 w-4" />,
          label: "Xuất Excel",
          variant: "outline",
          onClick: () =>
            exportToExcel(
              filteredPlates.filter((p: any) => selectedIds.has(p.plateId || p.id)).map(toDispatchExportRow),
              DISPATCH_EXPORT_COLUMNS,
              "dieu_phoi_kem_da_chon",
            ),
        },
        ...(hasPermission("plates", "delete")
          ? [
              {
                icon: <Trash2 className="h-4 w-4" />,
                label: `Xoá (${selectedCount})`,
                variant: "destructive" as const,
                onClick: () => {
                  const byKey = new Map(filteredPlates.map((p: any) => [p.plateId || p.id, p.id || p.plateId]));
                  executeBulkAction({
                    confirmFn: confirm,
                    selectedIds: Array.from(selectedIds).map((k) => byKey.get(k) || k),
                    actionFn: async (id) => (trpcClient as any).technical.plates.delete.mutate({ id }),
                    actionName: "xóa",
                    itemType: "bản kẽm",
                    onSuccess: () => {
                      clearSelection();
                      queryClient.invalidateQueries({ queryKey: ["plates"] });
                    },
                    requireConfirm: true,
                    confirmMessage: `Xóa ${selectedCount} bản kẽm đã chọn? Bản kẽm đã có lịch sử ghi/in sẽ không xóa được.`,
                  });
                },
              },
            ]
          : []),
      ]
    : [];

  const openProof = (plate: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPlate(plate);
    setShowProofModal(true);
  };

  const openMount = (plate: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPlate(plate);
    setShowMountModal(true);
  };

  const openDamage = (plate: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPlate(plate);
    setShowDamageModal(true);
  };

  const openPrint = (plate: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPlate(plate);
    setShowPrintModal(true);
  };

  return (
    <div className="space-y-3">
      {/* ═══ SINGLE-ROW INLINE METRICS BAR (RULE 4) ═══ */}
      <div className="flex items-center gap-2 overflow-x-auto py-1 px-1 text-xs">
        <button
          type="button"
          onClick={() => setStatusFilter("ALL")}
          className={cn(
            "h-8 px-3 rounded-lg border flex items-center gap-1.5 font-medium transition-all shrink-0",
            statusFilter === "ALL"
              ? "bg-slate-900 text-white border-slate-900 shadow-sm"
              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          )}
        >
          <span>Tất cả bộ kẽm:</span>
          <span className="font-bold">{metrics.total}</span>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter("PENDING_PROOF")}
          className={cn(
            "h-8 px-3 rounded-lg border flex items-center gap-1.5 font-medium transition-all shrink-0",
            statusFilter === "PENDING_PROOF"
              ? "bg-amber-600 text-white border-amber-600 shadow-sm"
              : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100/70"
          )}
        >
          <Clock className="w-3.5 h-3.5" />
          <span>Chờ Duyệt Proof:</span>
          <span className="font-bold font-mono">{metrics.pendingProof}</span>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter("PROOF_APPROVED")}
          className={cn(
            "h-8 px-3 rounded-lg border flex items-center gap-1.5 font-medium transition-all shrink-0",
            statusFilter === "PROOF_APPROVED"
              ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
              : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100/70"
          )}
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          <span>Proof Đã Ký:</span>
          <span className="font-bold font-mono">{metrics.proofApproved}</span>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter("IN_USE")}
          className={cn(
            "h-8 px-3 rounded-lg border flex items-center gap-1.5 font-medium transition-all shrink-0",
            statusFilter === "IN_USE"
              ? "bg-blue-600 text-white border-blue-600 shadow-sm"
              : "bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100/70"
          )}
        >
          <Printer className="w-3.5 h-3.5" />
          <span>Đang Trên Máy In:</span>
          <span className="font-bold font-mono">{metrics.mountedInUse}</span>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter("DAMAGED")}
          className={cn(
            "h-8 px-3 rounded-lg border flex items-center gap-1.5 font-medium transition-all shrink-0",
            statusFilter === "DAMAGED"
              ? "bg-rose-600 text-white border-rose-600 shadow-sm"
              : "bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100/70"
          )}
        >
          <ShieldAlert className="w-3.5 h-3.5" />
          <span>Sự Cố / Ghi Bù:</span>
          <span className="font-bold font-mono">{metrics.damagedRemake}</span>
        </button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="btn-export-excel"
          className="h-8 ml-auto shrink-0 text-xs bg-white"
          onClick={handleExportAll}
          disabled={filteredPlates.length === 0}
        >
          <Download className="h-3.5 w-3.5 mr-1" /> Xuất Excel
        </Button>
      </div>


      {/* ═══ BẢNG ĐIỀU PHỐI KẼM CTP (RULE 4 SOLID STICKY COLUMNS) ═══ */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
        <div className="overflow-x-auto max-h-[600px] erp-table-wrapper">
          <Table className={rowSelection.selectionMode ? "apv-select-mode" : undefined}>
            <TableHeader className="bg-slate-100/90 sticky top-0 z-30 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <TableRow className="border-b border-slate-200 text-xs">
                <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                {/* Sticky Left: Cột Mã bài in (Rule 4 Solid) */}
                <TableHead
                  style={{ backgroundColor: "#f1f5f9" }}
                  className="font-bold text-slate-800 min-w-[130px] sticky left-0 z-30 shadow-[2px_0_4px_rgba(0,0,0,0.06)] [isolation:isolate]"
                >
                  Mã Bài Kẽm
                </TableHead>
                <TableHead className="font-bold text-slate-700 min-w-[180px]">Sản Phẩm & Bài In</TableHead>
                <TableHead className="font-bold text-slate-700 min-w-[120px]">Khổ Kẽm / Tram</TableHead>
                <TableHead className="font-bold text-slate-700 min-w-[160px]">Bộ Phân Màu</TableHead>
                <TableHead className="font-bold text-slate-700 min-w-[130px] text-center">Duyệt Mẫu Proof</TableHead>
                <TableHead className="font-bold text-slate-700 min-w-[160px]">Máy In Offset Đích</TableHead>
                <TableHead className="font-bold text-slate-700 min-w-[120px] text-center">Trạng Thái Kẽm</TableHead>
                {/* Sticky Right: Cột Thao tác 1-chạm (Rule 4 Solid) */}
                <TableHead
                  style={{ backgroundColor: "#f1f5f9" }}
                  className="font-bold text-slate-800 min-w-[200px] text-right sticky right-0 z-30 shadow-[-2px_0_4px_rgba(0,0,0,0.06)] [isolation:isolate]"
                >
                  Điều Phối Tác Nghiệp
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-12 text-center text-slate-400">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-500" />
                    Đang tải dữ liệu điều phối kẽm CTP...
                  </TableCell>
                </TableRow>
              ) : filteredPlates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-12 text-center text-slate-400">
                    Không tìm thấy bài kẽm phù hợp với bộ lọc
                  </TableCell>
                </TableRow>
              ) : (
                filteredPlates.map((plate) => {
                  const plateKey = plate.plateId || plate.id;
                  const isProofApproved = plate.proofStatus === "APPROVED";
                  const isMounted = plate.status === "IN_USE";
                  const isDamaged = plate.status === "DAMAGED" || (plate.remakeCount && plate.remakeCount > 0);

                  return (
                    <DataTableRow
                      key={plateKey}
                      id={plateKey}
                      rowSelection={rowSelection}
                      onClick={() => navigate({ to: "/plates/$plateId", params: { plateId: plateKey } })}
                      className="cursor-pointer hover:bg-slate-50/80 text-xs border-b border-slate-100 transition-colors group"
                    >
                      <DataTableRowCheckbox id={plateKey} rowSelection={rowSelection} />
                      {/* Sticky Left: Cột Mã bài in (Rule 4 Solid #ffffff, hover #f8fafc) */}
                      <TableCell
                        style={{ backgroundColor: "#ffffff" }}
                        className="font-mono font-bold text-indigo-600 sticky left-0 z-20 shadow-[2px_0_4px_rgba(0,0,0,0.04)] group-hover:!bg-slate-50 [isolation:isolate]"
                      >
                        <div className="flex flex-col gap-0.5">
                          <span>{plateKey}</span>
                          {plate.version && plate.version > 1 && (
                            <span className="text-[10px] text-amber-600 font-sans">v{plate.version}</span>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                        <ProductionThumbnailCell
                          imageUrl={plate.product?.image}
                          layoutUrl={plate.image}
                          title={plate.productName || plate.name || undefined}
                          subtitle={plate.plateId}
                          type="product"
                          size={34}
                        />
                        <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate max-w-[200px]">
                          {plate.productName || plate.name || "Bản kẽm Offset"}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate max-w-[200px]">
                          {plate.customerName || plate.customer?.name || "—"}
                        </div>
                        </div>
                        </div>
                      </TableCell>

                      <TableCell className="font-mono text-slate-700">
                        <div>{plateStockType(plate.size)}</div>
                      </TableCell>

                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(plate.colors || ["Cyan", "Magenta", "Yellow", "Black"]).map((c: string, idx: number) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className={cn(
                                "text-[9px] px-1 py-0 h-4 border leading-none font-bold",
                                c.toLowerCase().includes("c") ? "bg-cyan-50 text-cyan-800 border-cyan-300" :
                                c.toLowerCase().includes("m") ? "bg-pink-50 text-pink-800 border-pink-300" :
                                c.toLowerCase().includes("y") ? "bg-yellow-50 text-yellow-800 border-yellow-300" :
                                c.toLowerCase().includes("k") ? "bg-slate-800 text-white border-slate-700" :
                                "bg-slate-100 text-slate-700"
                              )}
                            >
                              {c}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>

                      <TableCell className="text-center">
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px] py-0.5",
                            isProofApproved
                              ? "bg-emerald-50 text-emerald-800 border-emerald-300 font-semibold"
                              : plate.proofStatus === "REJECTED"
                              ? "bg-rose-50 text-rose-800 border-rose-300 font-semibold"
                              : "bg-amber-50 text-amber-800 border-amber-300"
                          )}
                        >
                          {isProofApproved ? "✓ Đã Duyệt" : plate.proofStatus === "REJECTED" ? "✗ Từ Chối" : "Chờ Duyệt"}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        {plate.activeMachineName ? (
                          <div className="flex flex-col">
                            <span className="font-bold text-blue-700 flex items-center gap-1">
                              <Printer className="w-3 h-3 text-blue-500" />
                              {plate.activeMachineName}
                            </span>
                            {plate.mountedBy && (
                              <span className="text-[10px] text-slate-500 font-sans">
                                Thợ: {plate.mountedBy}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">Chưa cấp phát máy</span>
                        )}
                      </TableCell>

                      <TableCell className="text-center">
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px] py-0.5 font-bold",
                            isDamaged
                              ? "bg-rose-100 text-rose-800 border-rose-300"
                              : isMounted
                              ? "bg-blue-100 text-blue-800 border-blue-300"
                              : plate.status === "AVAILABLE"
                              ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                              : "bg-slate-100 text-slate-700"
                          )}
                        >
                          {isDamaged
                            ? `Sự cố (${plate.remakeCount || 1} lần)`
                            : isMounted
                            ? "Đang In (IN_USE)"
                            : plate.status === "AVAILABLE"
                            ? "Sẵn Sàng"
                            : plate.status || "Chờ xử lý"}
                        </Badge>
                      </TableCell>

                      {/* Sticky Right: Cụm nút thao tác 1-chạm (Rule 4 Solid #ffffff, hover #f8fafc) */}
                      <TableCell
                        style={{ backgroundColor: "#ffffff" }}
                        className="text-right sticky right-0 z-20 shadow-[-2px_0_4px_rgba(0,0,0,0.04)] group-hover:!bg-slate-50 [isolation:isolate]"
                      >
                        <div className="flex items-center justify-end gap-1">
                          {/* Nút 1: Ký Duyệt Proof */}
                          <Button
                            size="sm"
                            variant="outline"
                            className={cn(
                              "h-7 px-2 text-[11px]",
                              isProofApproved
                                ? "text-emerald-700 border-emerald-200 bg-emerald-50/40 hover:bg-emerald-100/50"
                                : "text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100"
                            )}
                            onClick={(e) => openProof(plate, e)}
                            title="Ký duyệt mẫu in thử màu Proof"
                          >
                            <FileCheck className="w-3.5 h-3.5 mr-1" />
                            {isProofApproved ? "Đã Ký" : "Ký Proof"}
                          </Button>

                          {/* Nút 2: Lắp Máy In */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[11px] text-blue-700 border-blue-200 bg-blue-50/40 hover:bg-blue-100"
                            onClick={(e) => openMount(plate, e)}
                            title="Cấp phát bản kẽm lên máy in Offset"
                          >
                            <Printer className="w-3.5 h-3.5 mr-1 text-blue-600" />
                            Lắp Máy
                          </Button>

                          {/* Nút 3: Báo Hỏng / Ghi Bù */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-1.5 text-[11px] text-rose-700 border-rose-200 bg-rose-50/40 hover:bg-rose-100"
                            onClick={(e) => openDamage(plate, e)}
                            title="Báo hỏng kẽm & kích hoạt ghi bù cấp tốc"
                          >
                            <RotateCcw className="w-3.5 h-3.5 text-rose-600" />
                          </Button>

                          {/* Nút 4: In Phiếu Bàn Giao */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-1.5 text-[11px] text-indigo-700 border-indigo-200 bg-indigo-50/40 hover:bg-indigo-100"
                            onClick={(e) => openPrint(plate, e)}
                            title="In Phiếu Bàn Giao Bản Kẽm CTP ISO 9001"
                          >
                            <FileText className="w-3.5 h-3.5 text-indigo-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </DataTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />

      {/* Presentation Modals */}
      <ProofApprovalModal
        open={showProofModal}
        onOpenChange={setShowProofModal}
        plate={selectedPlate}
      />

      <MountPlateToMachineModal
        open={showMountModal}
        onOpenChange={setShowMountModal}
        plate={selectedPlate}
      />

      <PlateDamageRemakeModal
        open={showDamageModal}
        onOpenChange={setShowDamageModal}
        plate={selectedPlate}
      />

      <PrintDialog
        open={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        type="plate_handover"
        data={selectedPlate}
      />
    </div>
  );
}
